import React from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { fadeUp, scaleIn, EASE_OUT_EXPO } from '../motion/variants';

const MotionLink = motion.create(Link);

/*
  AuthShell — auth sayfaları (giriş, kayıt, şifremi unuttum, şifre sıfırlama,
  e-posta doğrulama, müşteri girişi) için ortak markalı kabuk.
  HomePage.js'teki .lp-hero / .nav-logo görsel diliyle birebir tutarlı:
  koyu navy arka plan + turuncu/menekşe blur orb'lar + camsı kart.
*/
const AuthShell = ({
  title,
  subtitle,
  icon = 'restaurant',
  maxWidth = 460,
  headerRight = null,
  footer = null,
  accentColor = '#f97316',
  children,
}) => {
  const reduceMotion = useReducedMotion();

  return (
    <div className="auth-shell-root">
      <style>{`
        .auth-shell-root, .auth-shell-root *, .auth-shell-root *::before, .auth-shell-root *::after {
          box-sizing: border-box;
        }

        .auth-shell-root {
          font-family: 'Inter', system-ui, sans-serif;
          min-height: 100vh;
          background: linear-gradient(160deg, #0d1117 0%, #161b27 55%, #1a1f2e 100%);
          color: #e2e8f0;
          position: relative;
          overflow-x: hidden;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: max(env(safe-area-inset-top, 0px), 32px) 20px 40px;
        }

        .auth-shell-orb {
          position: absolute;
          border-radius: 50%;
          pointer-events: none;
          filter: blur(120px);
        }
        .auth-shell-orb-1 { width: 620px; height: 620px; top: -220px; left: -220px; background: rgba(249,115,22,0.10); }
        .auth-shell-orb-2 { width: 480px; height: 480px; bottom: -180px; right: -140px; background: rgba(139,92,246,0.08); }
        .auth-shell-grid {
          position: absolute; inset: 0;
          background-image: linear-gradient(rgba(255,255,255,0.02) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.02) 1px, transparent 1px);
          background-size: 60px 60px;
          mask-image: radial-gradient(ellipse at center, black 25%, transparent 75%);
          pointer-events: none;
        }

        .auth-shell-topbar {
          position: relative;
          z-index: 2;
          width: 100%;
          max-width: var(--auth-shell-max-width, 460px);
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 28px;
        }

        .auth-shell-logo {
          display: flex; align-items: center; gap: 10px;
          text-decoration: none; color: inherit;
        }
        .auth-shell-logo-icon {
          width: 40px; height: 40px; border-radius: 12px;
          background: linear-gradient(135deg, #f97316, #ea580c);
          display: flex; align-items: center; justify-content: center;
          box-shadow: 0 4px 16px rgba(249,115,22,0.35);
          flex-shrink: 0;
        }
        .auth-shell-logo-text { font-size: 19px; font-weight: 800; color: #fff; white-space: nowrap; }
        .auth-shell-logo-text span { color: #f97316; }

        .auth-shell-back {
          display: inline-flex; align-items: center; gap: 6px;
          font-size: 13px; font-weight: 600; color: rgba(255,255,255,0.55);
          text-decoration: none; padding: 8px 12px; border-radius: 10px;
          transition: all 0.2s;
        }
        .auth-shell-back:hover { color: #fff; background: rgba(255,255,255,0.07); }

        .auth-shell-card {
          position: relative;
          z-index: 2;
          width: 100%;
          max-width: var(--auth-shell-max-width, 460px);
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.09);
          border-radius: 22px;
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          box-shadow: 0 24px 80px rgba(0,0,0,0.45);
          padding: clamp(28px, 5vw, 40px);
        }

        .auth-shell-header {
          text-align: center;
          margin-bottom: 28px;
        }
        .auth-shell-icon-wrap {
          width: 52px; height: 52px; border-radius: 14px;
          background: linear-gradient(135deg, var(--auth-shell-accent, #f97316), var(--auth-shell-accent-dark, #ea580c));
          margin: 0 auto 18px;
          display: flex; align-items: center; justify-content: center;
          box-shadow: 0 10px 28px -6px var(--auth-shell-accent-glow, rgba(249,115,22,0.45));
        }
        .auth-shell-title {
          font-size: clamp(22px, 4vw, 28px); font-weight: 900; color: #fff;
          letter-spacing: -0.02em; margin: 0 0 8px;
        }
        .auth-shell-subtitle {
          font-size: 14px; color: rgba(255,255,255,0.5); line-height: 1.6; margin: 0;
        }

        .auth-shell-footer {
          margin-top: 24px; text-align: center;
        }

        /* ── Ortak form elemanları (sayfalar bu class'ları kullanabilir) ── */
        .auth-field { margin-bottom: 16px; }
        .auth-field:last-child { margin-bottom: 0; }
        .auth-label {
          display: block; font-size: 13px; font-weight: 700; color: rgba(255,255,255,0.75);
          margin-bottom: 7px;
        }
        .auth-input-wrap { position: relative; }
        .auth-input-icon {
          position: absolute; left: 14px; top: 50%; transform: translateY(-50%);
          color: rgba(255,255,255,0.35); pointer-events: none;
        }
        .auth-input, .auth-select {
          width: 100%; height: 50px; padding: 0 16px 0 44px;
          border-radius: 12px; border: 1.5px solid rgba(255,255,255,0.1);
          background: rgba(255,255,255,0.04); color: #f1f5f9;
          font-size: 15px; font-family: inherit; outline: none;
          transition: all 0.2s; box-sizing: border-box;
        }
        .auth-input::placeholder { color: rgba(255,255,255,0.28); }
        .auth-input:focus, .auth-select:focus {
          border-color: rgba(249,115,22,0.55);
          background: rgba(249,115,22,0.06);
          box-shadow: 0 0 0 4px rgba(249,115,22,0.12);
        }
        .auth-input:disabled, .auth-select:disabled {
          opacity: 0.6; cursor: not-allowed;
        }
        .auth-select { cursor: pointer; }
        .auth-help { font-size: 11.5px; color: rgba(255,255,255,0.4); margin-top: 6px; line-height: 1.5; }

        .auth-btn-primary {
          width: 100%; height: 50px; border: none; border-radius: 12px;
          background: linear-gradient(135deg, #f97316, #ea580c);
          color: #fff; font-size: 15px; font-weight: 800; font-family: inherit;
          cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 8px;
          box-shadow: 0 8px 24px rgba(249,115,22,0.32);
        }
        .auth-btn-primary:disabled { opacity: 0.65; cursor: not-allowed; }

        .auth-divider { display: flex; align-items: center; gap: 12px; margin: 20px 0; }
        .auth-divider-line { flex: 1; height: 1px; background: rgba(255,255,255,0.1); }
        .auth-divider-text {
          font-size: 11px; font-weight: 700; color: rgba(255,255,255,0.35);
          text-transform: uppercase; letter-spacing: 0.08em;
        }

        .auth-link { color: #f97316; text-decoration: none; font-weight: 700; }
        .auth-link:hover { text-decoration: underline; }

        .auth-tabs {
          display: flex; background: rgba(255,255,255,0.05);
          border: 1px solid rgba(255,255,255,0.08);
          padding: 4px; border-radius: 12px; margin-bottom: 24px;
        }
        .auth-tab {
          flex: 1; padding: 10px; font-size: 13px; font-weight: 700;
          border: none; border-radius: 8px; cursor: pointer;
          background: transparent; color: rgba(255,255,255,0.5);
          transition: all 0.2s; font-family: inherit;
        }
        .auth-tab.active {
          background: rgba(249,115,22,0.16);
          color: #fb923c;
          box-shadow: 0 0 0 1px rgba(249,115,22,0.3);
        }

        .auth-alert {
          padding: 14px 16px; border-radius: 12px; margin-bottom: 20px;
          font-size: 13.5px; line-height: 1.5;
        }
        .auth-alert.success { background: rgba(16,185,129,0.1); color: #6ee7b7; border: 1px solid rgba(16,185,129,0.3); }
        .auth-alert.error { background: rgba(239,68,68,0.1); color: #fca5a5; border: 1px solid rgba(239,68,68,0.3); }
        .auth-alert.info { background: rgba(249,115,22,0.08); color: #fdba74; border: 1px solid rgba(249,115,22,0.25); }
      `}</style>

      <div className="auth-shell-orb auth-shell-orb-1" />
      <div className="auth-shell-orb auth-shell-orb-2" />
      <div className="auth-shell-grid" />

      <div className="auth-shell-topbar" style={{ '--auth-shell-max-width': `${maxWidth}px` }}>
        <MotionLink
          to="/"
          className="auth-shell-logo"
          whileHover={reduceMotion ? undefined : { y: -1 }}
          transition={{ duration: 0.2 }}
        >
          <div className="auth-shell-logo-icon">
            <span className="material-symbols-outlined" style={{ color: '#fff', fontSize: 20 }}>{icon}</span>
          </div>
          <span className="auth-shell-logo-text">QR<span>ezervasyon</span></span>
        </MotionLink>
        {headerRight || (
          <Link to="/" className="auth-shell-back">
            <span className="material-symbols-outlined" style={{ fontSize: 16 }}>arrow_back</span>
            Ana Sayfa
          </Link>
        )}
      </div>

      <motion.div
        className="auth-shell-card"
        style={{
          '--auth-shell-max-width': `${maxWidth}px`,
          '--auth-shell-accent': accentColor,
        }}
        variants={reduceMotion ? fadeUp : scaleIn}
        initial="hidden"
        animate="show"
        transition={{ duration: 0.5, ease: EASE_OUT_EXPO }}
      >
        {(title || subtitle) && (
          <div className="auth-shell-header">
            <div className="auth-shell-icon-wrap">
              <span className="material-symbols-outlined" style={{ color: '#fff', fontSize: 26 }}>{icon}</span>
            </div>
            {title && <h1 className="auth-shell-title">{title}</h1>}
            {subtitle && <p className="auth-shell-subtitle">{subtitle}</p>}
          </div>
        )}

        {children}

        {footer && <div className="auth-shell-footer">{footer}</div>}
      </motion.div>
    </div>
  );
};

export default AuthShell;
